import React, { useState, useEffect } from 'react';
import { paisesAPI } from '../../services/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Globe2, Loader2, X, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatApiError } from '../../utils/apiError';

const INITIAL_FORM = {
  codigo: '',
  nombre: '',
  bandera: '',
  sigla_autoridad: '',
  nombre_autoridad: '',
  provincias: [],
  activo: true,
  orden: 99,
};

const AdminPaises = () => {
  const navigate = useNavigate();
  const [paises, setPaises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [nuevaProvincia, setNuevaProvincia] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const r = await paisesAPI.listar();
      setPaises(r.data.items || []);
    } catch (e) {
      toast.error(formatApiError(e, 'No se pudieron cargar los países'));
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setFormData(INITIAL_FORM);
    setNuevaProvincia('');
    setShowForm(true);
  };

  const openEdit = (p) => {
    setEditingId(p.id);
    setFormData({
      codigo: p.codigo,
      nombre: p.nombre,
      bandera: p.bandera || '',
      sigla_autoridad: p.sigla_autoridad,
      nombre_autoridad: p.nombre_autoridad || '',
      provincias: [...(p.provincias || [])],
      activo: p.activo,
      orden: p.orden ?? 99,
    });
    setNuevaProvincia('');
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!formData.codigo.trim() || !formData.nombre.trim() || !formData.sigla_autoridad.trim()) {
      toast.error('Código, nombre y sigla son obligatorios');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...formData, codigo: formData.codigo.toUpperCase() };
      if (editingId) {
        // Code no es editable después de crear → quitarlo del payload
        const { codigo, ...rest } = payload;
        await paisesAPI.actualizar(editingId, rest);
        toast.success(`País "${formData.nombre}" actualizado`);
      } else {
        await paisesAPI.crear(payload);
        toast.success(`País "${formData.nombre}" creado`);
      }
      setShowForm(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e, 'Error al guardar el país'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`¿Eliminar "${p.nombre}"? Esta acción no se puede deshacer.`)) return;
    try {
      await paisesAPI.eliminar(p.id);
      toast.success(`${p.nombre} eliminado`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e, 'No se pudo eliminar'));
    }
  };

  const toggleActivo = async (p) => {
    try {
      await paisesAPI.actualizar(p.id, { activo: !p.activo });
      toast.success(`${p.nombre} ${!p.activo ? 'activado' : 'desactivado'}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e, 'No se pudo cambiar el estado'));
    }
  };

  const addProvincia = () => {
    const v = nuevaProvincia.trim();
    if (!v) return;
    if (formData.provincias.includes(v)) {
      toast.error('Esa provincia ya existe');
      return;
    }
    setFormData({ ...formData, provincias: [...formData.provincias, v] });
    setNuevaProvincia('');
  };

  const removeProvincia = (prov) => {
    setFormData({ ...formData, provincias: formData.provincias.filter(p => p !== prov) });
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-6xl mx-auto py-6 px-4">
        <div className="mb-6 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/admin')}
              data-testid="back-to-admin-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Panel
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Globe2 className="h-6 w-6 text-teal-600" />
                Países y autoridades aeronáuticas
              </h1>
              <p className="text-sm text-gray-600">
                Gestioná los países habilitados, sus banderas, siglas de autoridad y provincias/departamentos.
              </p>
            </div>
          </div>
          <Button onClick={openCreate} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="create-pais-btn">
            <Plus className="h-4 w-4 mr-1" /> Nuevo país
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {paises.map(p => (
              <Card key={p.id} className={`${p.activo ? 'border-teal-200' : 'border-gray-300 bg-gray-50'}`} data-testid={`pais-card-${p.codigo}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-3xl leading-none">{p.bandera || '🏳️'}</span>
                        <div>
                          <h3 className="text-lg font-bold text-gray-900">{p.nombre}</h3>
                          <p className="text-xs text-gray-500">Código: {p.codigo}</p>
                        </div>
                      </div>
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <Badge className="bg-teal-100 text-teal-800 border-teal-200">
                          {p.sigla_autoridad}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {p.provincias?.length || 0} provincias
                        </Badge>
                        {p.activo ? (
                          <Badge className="bg-green-100 text-green-800 border-green-200">Activo</Badge>
                        ) : (
                          <Badge className="bg-gray-200 text-gray-700">Inactivo</Badge>
                        )}
                      </div>
                      {p.nombre_autoridad && (
                        <p className="text-xs text-gray-600 mt-2 italic">{p.nombre_autoridad}</p>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs text-gray-600">Activo</span>
                        <Switch
                          checked={p.activo}
                          onCheckedChange={() => toggleActivo(p)}
                          data-testid={`toggle-activo-${p.codigo}`}
                        />
                      </div>
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" onClick={() => openEdit(p)} data-testid={`edit-pais-${p.codigo}`}>
                          <Edit className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => handleDelete(p)} data-testid={`delete-pais-${p.codigo}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Editar país' : 'Nuevo país'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Código ISO * <span className="text-xs text-gray-500">(2-3)</span></Label>
                <Input
                  value={formData.codigo}
                  onChange={(e) => setFormData({...formData, codigo: e.target.value.toUpperCase().slice(0, 3)})}
                  placeholder="AR"
                  disabled={!!editingId}
                  data-testid="form-codigo"
                />
              </div>
              <div className="col-span-2">
                <Label>Nombre *</Label>
                <Input
                  value={formData.nombre}
                  onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                  placeholder="Argentina"
                  data-testid="form-nombre"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Bandera (emoji)</Label>
                <Input
                  value={formData.bandera}
                  onChange={(e) => setFormData({...formData, bandera: e.target.value})}
                  placeholder="🇦🇷"
                  data-testid="form-bandera"
                />
              </div>
              <div>
                <Label>Sigla autoridad *</Label>
                <Input
                  value={formData.sigla_autoridad}
                  onChange={(e) => setFormData({...formData, sigla_autoridad: e.target.value.toUpperCase()})}
                  placeholder="ANAC"
                  data-testid="form-sigla"
                />
              </div>
              <div>
                <Label>Orden</Label>
                <Input
                  type="number"
                  value={formData.orden}
                  onChange={(e) => setFormData({...formData, orden: parseInt(e.target.value || '0', 10)})}
                  data-testid="form-orden"
                />
              </div>
            </div>

            <div>
              <Label>Nombre completo de la autoridad</Label>
              <Input
                value={formData.nombre_autoridad}
                onChange={(e) => setFormData({...formData, nombre_autoridad: e.target.value})}
                placeholder="Administración Nacional de Aviación Civil"
                data-testid="form-nombre-autoridad"
              />
            </div>

            <div className="flex items-center gap-2 py-2">
              <Switch
                checked={formData.activo}
                onCheckedChange={(v) => setFormData({...formData, activo: v})}
                data-testid="form-activo"
              />
              <Label className="cursor-pointer" onClick={() => setFormData({...formData, activo: !formData.activo})}>
                Activo (visible en dropdowns públicos)
              </Label>
            </div>

            <div>
              <Label>Provincias / Departamentos / Estados</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={nuevaProvincia}
                  onChange={(e) => setNuevaProvincia(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addProvincia(); } }}
                  placeholder="Ej: Buenos Aires"
                  data-testid="form-nueva-provincia"
                />
                <Button type="button" onClick={addProvincia} variant="outline" data-testid="add-provincia-btn">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-3 max-h-48 overflow-y-auto p-2 bg-gray-50 rounded border">
                {formData.provincias.length === 0 && (
                  <span className="text-xs text-gray-500 italic">No hay provincias cargadas todavía</span>
                )}
                {formData.provincias.map(prov => (
                  <Badge
                    key={prov}
                    variant="secondary"
                    className="flex items-center gap-1 cursor-pointer hover:bg-red-100"
                    onClick={() => removeProvincia(prov)}
                    data-testid={`provincia-chip-${prov.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {prov}
                    <X className="h-3 w-3" />
                  </Badge>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {formData.provincias.length} cargadas · Click para quitar
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)} disabled={saving}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="save-pais-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
              {editingId ? 'Guardar cambios' : 'Crear país'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPaises;
